import type { FC } from "react";
import { useMemo } from "react";

import dayjs from "dayjs";
import { renderToString } from "react-dom/server";

import { shortMonthAndShortYear, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import type { IPresenceBreakdownGraphTooltipProps } from "app/pages/digital-marketing/keywords-campaigns/reports/serp-feature-report/SERPReportTypes";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import { TooltipWrapper } from "app/pages/digital-marketing/keywords-campaigns/reports/serp-feature-report/serp-feature-presence/SerpFeaturePresenceStyled";
import type { Point } from "highcharts";

const GRAPH_HEIGHT = 280;

interface TooltipRow {
    serpFeature: { label: string; color: string };
    keywordsCount: { value: string };
}

const getTooltipColumnDefinitions = (): Array<ColumnDefinition<TooltipRow>> => {
    const tooltipHeaderFeatureTitle = translate(
        "website.analysis.seo.overview.serp_features_composition.graph.tooltip.header.feature",
    );

    const tooltipHeaderCount = translate(
        "website.analysis.seo.overview.serp_features_composition.graph.tooltip.header.count",
    );

    return [
        {
            key: tooltipHeaderFeatureTitle,
            title: tooltipHeaderFeatureTitle,
            selector: "serpFeature" as const,
            align: "left",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ serpFeature }) => (
                <ChartTooltip.LabelCell
                    label={serpFeature.label}
                    backgroundColor={serpFeature.color}
                />
            ),
        },
        {
            key: tooltipHeaderCount,
            title: tooltipHeaderCount,
            selector: "keywordsCount" as const,
            align: "right",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({ keywordsCount }) => <ChartTooltip.ValueCell value={keywordsCount.value} />,
        },
    ];
};

const SerpFeaturesCompositionGraphTooltip: FC<
    Omit<IPresenceBreakdownGraphTooltipProps, "points"> & { points: { color: string; name: string; value: number }[] }
> = ({ points, date }) => {
    const data = points.map((point) => ({
        serpFeature: {
            label: point.name,
            color: point.color,
        },
        keywordsCount: {
            value: numberFilter()(point.value),
        },
    }));

    const tooltipColumnDefinitions = useMemo(() => getTooltipColumnDefinitions(), []);

    return (
        <TooltipWrapper>
            <ChartTooltip<TooltipRow>
                width={250}
                title={dayjs.utc(date).format(shortMonthDayAndYear)}
                columnDefinition={tooltipColumnDefinitions}
                data={data}
            />
        </TooltipWrapper>
    );
};

export const getChartConfig = () => {
    const yAxisFormatter = ({ value }) => numberFilter()(value as number);
    const xAxisFormatter = ({ value }) => dayjs.utc(value as number).format(shortMonthAndShortYear);

    const config = {
        chart: {
            height: GRAPH_HEIGHT,
            marginTop: 0,
            marginBottom: 0,
            marginRight: 24,
        },
        tooltip: {
            shared: true,
            outside: true,
            useHTML: true,
            formatter(this: Point) {
                const points = this.points.map((point) => {
                    return {
                        color: point.color as string,
                        name: point.series.name,
                        value: point.y,
                    };
                });
                return renderToString(
                    <SerpFeaturesCompositionGraphTooltip
                        points={points}
                        date={this.x}
                    />,
                );
            },
        },
        yAxis: {
            showFirstLabel: true,
            startOnTick: true,
            labels: {
                formatter: yAxisFormatter,
            },
        },
        xAxis: {
            labels: {
                formatter: xAxisFormatter,
            },
        },
    };

    return config;
};
